/**
 * Feature Flags Utility
 * 
 * This utility provides core functions for evaluating feature flags.
 */

/** Dev bypass — when true, all feature checks pass (mirrors REACT_APP_PERMISSION_BYPASS). */
export const FEATURE_FLAG_BYPASS =
  process.env.REACT_APP_FEATURE_FLAG_BYPASS === 'true';

/**
 * Check if a feature is enabled
 * @param {object} featureFlags - Feature flags object
 * @param {string} featureName - Name of the feature to check
 * @returns {boolean} - True if feature is enabled
 */
export function hasFeature(featureFlags, featureName) {
  if (FEATURE_FLAG_BYPASS) {
    return true;
  }

  if (!featureFlags) {
    return true; // Fail open - allow access if no flags available
  }
  
  return featureFlags[featureName] === true;
}

/**
 * Filter navigation items based on feature flags
 * @param {Array} navItems - Array of navigation items
 * @param {object} featureFlags - Feature flags object
 * @param {object} featureMap - Map of menu items to feature flags
 * @returns {Array} - Filtered navigation items
 */
export function filterNavItemsByFeatureFlags(navItems, featureFlags, featureMap = {}) {
  if (!navItems || navItems.length === 0) {
    return [];
  }
  
  return navItems
    .map((item) => {
      // Get the feature flag for this menu item
      const featureFlag = featureMap[item.menuItem];
      
      // If no feature flag mapping exists, include the item
      if (!featureFlag) {
        // Still filter dropdown items if they exist
        if (item.ddItems) {
          const filteredDropdown = filterNavItemsByFeatureFlags(item.ddItems, featureFlags, featureMap);
          
          if (filteredDropdown.length === 0) {
            return null;
          }
          
          return {
            ...item,
            ddItems: filteredDropdown,
          };
        }
        return item;
      }
      
      // Check if feature is enabled
      if (!hasFeature(featureFlags, featureFlag)) {
        return null; // Filter out this item
      }
      
      // If item has dropdown items, filter them too
      if (item.ddItems) {
        const filteredDropdown = filterNavItemsByFeatureFlags(
          item.ddItems,
          featureFlags,
          featureMap
        );
        
        // If all dropdown items are filtered out, filter the parent too
        if (filteredDropdown.length === 0) {
          return null;
        }
        
        return {
          ...item,
          ddItems: filteredDropdown,
        };
      }
      
      return item;
    })
    .filter(Boolean); // Remove null items
}
